import { useEffect, useMemo, useState } from "react";
import { useT } from "../i18n/useT";
import { useSettings } from "./useSettings";
import { getNotificationPermission, requestNotificationPermission } from "../notifications/permission";

function uniqSorted(nums: number[]) {
  const set = new Set(nums.filter((x) => Number.isFinite(x) && x > 0).map((x) => Math.round(x)));
  return Array.from(set).sort((a, b) => b - a); // bigger first (day -> hours -> minutes)
}

export function RemindersCard() {
  const t = useT();
  const [s, setS] = useSettings();
  const [value, setValue] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [firstLessonValue, setFirstLessonValue] = useState("");
  const [otherLessonsValue, setOtherLessonsValue] = useState("");

  const perm = getNotificationPermission();

  const minutes = useMemo(() => uniqSorted(s.deadlineReminderMinutes || []), [s.deadlineReminderMinutes]);
  const presets = [1440, 180, 60, 30, 15];

  useEffect(() => {
    setFirstLessonValue(s.firstLessonReminderMinutes != null ? String(s.firstLessonReminderMinutes) : "");
  }, [s.firstLessonReminderMinutes]);

  useEffect(() => {
    const v = s.lessonReminderMinutes && s.lessonReminderMinutes[0];
    setOtherLessonsValue(v != null ? String(v) : "");
  }, [s.lessonReminderMinutes]);

  const addMinutes = (n: number) => {
    if (!Number.isFinite(n) || n <= 0) return;
    setS({ deadlineReminderMinutes: uniqSorted([...(s.deadlineReminderMinutes || []), n]) });
  };

  const add = () => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return;
    addMinutes(n);
    setValue("");
  };

  const remove = (n: number) => {
    setS({ deadlineReminderMinutes: (s.deadlineReminderMinutes || []).filter((x) => x !== n) });
  };

  return (
    <div className="settings-card">
      {/* Header Section */}
      <div className="reminders-header">
        <h3>{t("settings.reminders")}</h3>
        <div className="permission-status">
          <span className="muted small">
            {t("settings.reminders.permission")}: <b>{perm}</b>
          </span>
        </div>
        <button
          className={`btn ghost ${requesting ? "is-loading" : ""}`}
          onClick={async () => {
            setRequesting(true);
            try {
              await requestNotificationPermission();
            } finally {
              window.setTimeout(() => setRequesting(false), 600);
            }
          }}
          disabled={requesting}
        >
          {t("settings.reminders.permission.request")}
        </button>
      </div>

      {/* Main Toggle */}
      <div className="block toggle-control">
        <button
          className={`btn ${s.remindersEnabled ? "primary" : "ghost"}`}
          onClick={async () => {
            if (!s.remindersEnabled) {
              const nextPerm = await requestNotificationPermission();
              if (nextPerm !== "granted") return;
            }
            setS({ remindersEnabled: !s.remindersEnabled });
          }}
        >
          {s.remindersEnabled ? "✓ " : ""}{t("settings.reminders.enable")}
        </button>
        <div className="muted small">{t("settings.reminders.permission.hint")}</div>
      </div>

      {/* Reminder Types */}
      <div className="reminders-types">
        <div className="reminders-type-section">
          <button
            className={`type-toggle btn ${s.deadlineRemindersEnabled ? "primary" : "ghost"}`}
            onClick={() => setS({ deadlineRemindersEnabled: !s.deadlineRemindersEnabled })}
            disabled={!s.remindersEnabled}
          >
            {s.deadlineRemindersEnabled ? "✓ " : ""}{t("settings.reminders.deadlines")}
          </button>

          {/* Deadlines Section - Collapsible */}
          {s.deadlineRemindersEnabled && (
            <div className="deadlines-content">
              {/* Presets Section */}
              <div className="presets-section">
                <label className="presets-label muted small">
                  {t("settings.reminders.deadlineMinutes")}
                </label>
                <div className="presets-chips">
                  {presets.map((m) => (
                    <button
                      key={m}
                      className="chip"
                      onClick={() => addMinutes(m)}
                      disabled={!s.remindersEnabled}
                      title={t("settings.reminders.add")}
                    >
                      {m}m
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Input Section */}
              <div className="custom-input-section">
                <div className="row wrap">
                  <input
                    className="input"
                    style={{ maxWidth: 140 }}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={t("settings.reminders.addPlaceholder")}
                    inputMode="numeric"
                    disabled={!s.remindersEnabled}
                  />
                  <button
                    className="btn primary"
                    onClick={add}
                    disabled={!s.remindersEnabled}
                  >
                    {t("settings.reminders.add")}
                  </button>
                </div>
              </div>

              {/* Active Reminders Section */}
              <div className="active-reminders">
                <label className="active-label muted small">
                  {t("settings.reminders.deadlineHint")}
                </label>
                <div className="active-chips">
                  {minutes.length === 0 ? (
                    <span className="muted small">-</span>
                  ) : (
                    minutes.map((m) => (
                      <button
                        key={m}
                        className="active-chip"
                        onClick={() => remove(m)}
                        title={t("common.remove")}
                      >
                        {m}m
                        <span className="remove-icon">×</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lessons Section - Collapsible */}
        <div className="reminders-type-section">
          <button
            className={`type-toggle btn ${s.lessonRemindersEnabled ? "primary" : "ghost"}`}
            onClick={() => setS({ lessonRemindersEnabled: !s.lessonRemindersEnabled })}
            disabled={!s.remindersEnabled}
          >
            {s.lessonRemindersEnabled ? "✓ " : ""}{t("settings.reminders.lessons")}
          </button>

          {s.lessonRemindersEnabled && (
            <div className="lessons-content">
              <div className="lesson-input-group">
                <label htmlFor="first-lesson-input" className="muted small">
                  {t("settings.reminders.firstLesson")}
                </label>
                <div className="row wrap">
                  <input
                    id="first-lesson-input"
                    className="input"
                    type="number"
                    min={1}
                    max={180}
                    value={firstLessonValue}
                    onChange={(e) => setFirstLessonValue(e.target.value)}
                    disabled={!s.remindersEnabled}
                  />
                  <button
                    className="btn primary"
                    onClick={() => {
                      const n = Number(firstLessonValue);
                      setS({ firstLessonReminderMinutes: Number.isFinite(n) && n > 0 ? n : undefined });
                    }}
                    disabled={!s.remindersEnabled}
                  >
                    OK
                  </button>
                </div>
              </div>

              <div className="lesson-input-group">
                <label htmlFor="other-lessons-input" className="muted small">
                  {t("settings.reminders.otherLessons")}
                </label>
                <div className="row wrap">
                  <input
                    id="other-lessons-input"
                    className="input"
                    type="number"
                    min={1}
                    max={180}
                    value={otherLessonsValue}
                    onChange={(e) => setOtherLessonsValue(e.target.value)}
                    disabled={!s.remindersEnabled}
                  />
                  <button
                    className="btn primary"
                    onClick={() => {
                      const n = Number(otherLessonsValue);
                      setS({ lessonReminderMinutes: Number.isFinite(n) && n > 0 ? [n] : [] });
                    }}
                    disabled={!s.remindersEnabled}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
